import React, { createContext, useState, useContext, useEffect } from 'react';
import { useToast } from './ToastContext';
import { useUserAuth } from './UserAuthContext';
import * as WishlistService from '../services/WishlistService';

const WishlistContext = createContext();

export const useWishlist = () => useContext(WishlistContext);

export const WishlistProvider = ({ children }) => {
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const { showSuccessToast, showErrorToast } = useToast();
  const { isAuthenticated } = useUserAuth();

  // Load wishlist data
  const loadWishlist = async () => {
    setLoading(true);
    try {
      if (isAuthenticated) {
        // Get wishlist from API
        const items = await WishlistService.getWishlistItems();
        // Only filter out completely invalid items
        const filteredItems = items.filter(item => 
          item && (item.name || item.product_id || item.id)
        );
        setWishlist(filteredItems);
      } else {
        // Get wishlist from localStorage
        const savedWishlist = localStorage.getItem('wishlist');
        if (savedWishlist) {
          const parsedWishlist = JSON.parse(savedWishlist);
          // Only filter out completely invalid items
          const filteredWishlist = parsedWishlist.filter(item => 
            item && (item.name || item.product_id || item.id)
          );
          setWishlist(filteredWishlist);
        }
      }
    } catch (error) {
      console.error('Error loading wishlist:', error);
      showErrorToast('Failed to load your wishlist');
    } finally {
      setLoading(false);
    }
  };

  // Load wishlist on mount and when auth state changes
  useEffect(() => {
    loadWishlist();
  }, [isAuthenticated]);

  // Save to localStorage when wishlist changes (only if not authenticated)
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      try {
        localStorage.setItem('wishlist', JSON.stringify(wishlist));
      } catch (error) {
        console.error('Error saving wishlist to localStorage:', error);
        showErrorToast('Failed to save your wishlist');
      }
    }
  }, [wishlist, loading, isAuthenticated]);

  const addToWishlist = async (productOrId) => {
    try {
      if (isAuthenticated) {
        // If authenticated, use the API
        const productId = typeof productOrId === 'object' ? productOrId.id : productOrId;
        const success = await WishlistService.addToWishlist(productId);
        if (success) {
          // Reload wishlist to get updated data from server
          await loadWishlist();
          const productName = typeof productOrId === 'object' ? productOrId.name : 'Item';
          showSuccessToast(`${productName} added to wishlist`);
        }
      } else {
        // Otherwise use localStorage
        if (typeof productOrId !== 'object') {
          throw new Error('Product object required for local storage');
        }
        setWishlist((prev) => {
          if (!prev.some(item => item.id === productOrId.id)) {
            showSuccessToast(`${productOrId.name} added to wishlist`);
            return [...prev, productOrId];
          }
          return prev;
        });
      }
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      showErrorToast('Failed to add item to wishlist');
    }
  };

  const removeFromWishlist = async (productId, productName = 'Item') => {
    try {
      if (isAuthenticated) {
        // If authenticated, use the API
        const success = await WishlistService.removeFromWishlist(productId);
        if (success) {
          // Reload wishlist to get updated data from server
          await loadWishlist();
          showSuccessToast(`${productName} removed from wishlist`);
        }
      } else {
        // Otherwise use localStorage
        setWishlist((prev) => {
          const product = prev.find(item => item.id === productId);
          if (product) {
            showSuccessToast(`${product.name} removed from wishlist`);
          }
          return prev.filter(item => item.id !== productId);
        });
      }
    } catch (error) {
      console.error('Error removing from wishlist:', error);
      showErrorToast('Failed to remove item from wishlist');
    }
  };

  const isInWishlist = async (productId) => {
    try {
      if (isAuthenticated) {
        return await WishlistService.isInWishlist(productId);
      } else {
        return wishlist.some(item => item.id === productId);
      }
    } catch (error) {
      console.error('Error checking wishlist:', error);
      return false;
    }
  };

  return (
    <WishlistContext.Provider value={{ 
      wishlist, 
      addToWishlist, 
      removeFromWishlist, 
      isInWishlist,
      loading,
      refreshWishlist: loadWishlist
    }}>
      {children}
    </WishlistContext.Provider>
  );
}; 